import React from 'react';
import { Box, Typography, Button, Stack } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { getAuthenticatedUploadUrl } from '../../api/client';
import { MSN } from './msnTheme';

function resolveMediaUrl(fileUrl) {
  if (!fileUrl) return '';
  if (/^https?:\/\//i.test(fileUrl) && !/\/uploads\//i.test(fileUrl)) return fileUrl;
  return getAuthenticatedUploadUrl(fileUrl);
}

function isImageMessage(message) {
  if (message.type === 'photo') return true;
  const name = (message.fileName || message.fileUrl || '').toLowerCase();
  return /\.(png|jpe?g|gif|webp|bmp)(\?|$)/i.test(name);
}

function isAudioMessage(message) {
  if (message.type === 'audio') return true;
  const name = (message.fileName || message.fileUrl || '').toLowerCase();
  return /\.(webm|mp3|ogg|wav|m4a)(\?|$)/i.test(name);
}

function isPdfMessage(message) {
  const name = (message.fileName || message.fileUrl || '').toLowerCase();
  return /\.pdf(\?|$)/i.test(name) || (message.mimeType || '').includes('pdf');
}

/**
 * Bolha de mensagem — texto + mídia (FASE 4).
 * Campos: type, content, fileUrl, fileName (socket / ChatContext).
 */
export default function MessageBubble({ message, isOwn, userName }) {
  const bgColor = isOwn ? MSN.bubbleSent : MSN.bubbleReceived;
  const borderColor = isOwn ? MSN.bubbleSentBorder : MSN.bubbleReceivedBorder;
  const mediaUrl = resolveMediaUrl(message.fileUrl);
  const hasMedia = Boolean(message.fileUrl);
  const showText =
    Boolean(message.content) &&
    (message.type === 'text' || hasMedia);

  const image = hasMedia && isImageMessage(message);
  const audio = hasMedia && isAudioMessage(message);
  const file = hasMedia && !image && !audio;
  const pdf = file && isPdfMessage(message);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isOwn ? 'flex-end' : 'flex-start',
        px: 1,
        py: 0.5
      }}
    >
      <Box
        sx={{
          maxWidth: audio || image ? 320 : '80%',
          minWidth: audio ? 220 : undefined,
          bgcolor: bgColor,
          borderRadius: 2,
          px: 1.5,
          py: 1,
          border: `1px solid ${borderColor}`,
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
        }}
      >
        {!isOwn && userName && (
          <Typography
            variant="caption"
            sx={{ color: MSN.textSecondary, display: 'block', mb: 0.5, fontSize: 11 }}
          >
            {userName}
          </Typography>
        )}

        <Stack spacing={1}>
          {showText && (
            <Typography
              variant="body2"
              sx={{
                color: MSN.textPrimary,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: 13
              }}
            >
              {message.content}
            </Typography>
          )}

          {image && mediaUrl && (
            <Box
              component="a"
              href={mediaUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: 'block', lineHeight: 0 }}
            >
              <Box
                component="img"
                src={mediaUrl}
                alt={message.fileName || 'Imagem'}
                sx={{
                  display: 'block',
                  maxWidth: 220,
                  maxHeight: 280,
                  width: '100%',
                  height: 'auto',
                  borderRadius: '8px',
                  objectFit: 'cover',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              />
            </Box>
          )}

          {audio && mediaUrl && (
            <Box sx={{ width: '100%', minWidth: 200 }}>
              <audio controls src={mediaUrl} style={{ width: '100%', maxWidth: '100%', height: 36 }} />
            </Box>
          )}

          {file && mediaUrl && (
            <Button
              variant="outlined"
              size="small"
              startIcon={pdf ? <PictureAsPdfIcon /> : <InsertDriveFileIcon />}
              href={mediaUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                borderColor: 'divider',
                color: 'text.primary',
                bgcolor: 'background.paper',
                maxWidth: '100%',
                '& .MuiButton-startIcon': { color: pdf ? 'error.main' : 'primary.main' }
              }}
            >
              <Typography
                component="span"
                variant="body2"
                noWrap
                sx={{ maxWidth: 180, fontSize: 12 }}
              >
                {message.fileName || (pdf ? 'Documento PDF' : 'Baixar arquivo')}
              </Typography>
            </Button>
          )}
        </Stack>

        <Typography
          variant="caption"
          sx={{ color: MSN.textMuted, display: 'block', mt: 0.75, fontSize: 10 }}
        >
          {message.createdAt
            ? new Date(message.createdAt).toLocaleTimeString('pt-BR', {
                hour: '2-digit',
                minute: '2-digit'
              })
            : ''}
          {message.readAt && ' · Lida'}
        </Typography>
      </Box>
    </Box>
  );
}
